import { useState, useRef, useEffect } from 'react';
import { ChevronDown, FileText, Trash2, Link2, Check, Copy, Download, Upload, MoreVertical, Send, Archive, ArchiveRestore } from 'lucide-react';
import { useNavigate } from 'react-router';
import { deleteVorgang, updateVorgang, downloadVorgangZip, fetchFileBlob, submitVnToBafa, getBafaEinreichStand, type BafaEinreichStand, type Vorgang } from '../../lib/vorgangApi';
import { getRemainingTimeBadge, remainingBadgeClasses, isGreenStatus } from '../../lib/remainingTime';
import { DocumentGrid } from './DocumentGrid';
import { VORGANG_SOLL, VORGANG_CATEGORIES, sollStand } from './documentSets';
import { naechsteAktion } from './naechsteAktion';
import { api } from '../../lib/api';
import { VnButton } from './VnButton';
import { DataRequestModal } from '../DataRequestModal';

// Berater bestätigt per Ein-Klick-Knopf im Dashboard "liegt bei BAFA, nichts zu tun" (siehe
// STATUS_BEI_BAFA in Dashboard.tsx) — derselbe String, hier dupliziert statt importiert, da
// Dashboard.tsx und dieses Modul keine gemeinsame "Status"-Konstantendatei teilen.
const STATUS_BEI_BAFA = 'Bei BAFA zur Prüfung';

// Farbe nach BAFA-Status (wie in der Berichte-Liste).
function statusBadge(s: string): string {
  switch (s) {
    case 'Abgeschlossen':
    case 'Ausgezahlt':
    case 'Zahlung erfolgt': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'Abgelehnt': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    case 'Neu': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case '—': return 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400';
    // Neutral statt amber — von uns hängt gerade nichts ab, kein Alarm nötig (Berg Energie).
    case STATUS_BEI_BAFA: return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300';
    default: return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }
}

export function VorgangAccordion({ vorgang, onChanged, customerEmail, open, onToggle, scrollBeimOeffnen }: {
  vorgang: Vorgang;
  onChanged: () => void;
  customerEmail?: string | null;
  /** Gesteuert von der Liste — es ist immer nur ein Vorgang offen. */
  open: boolean;
  onToggle: () => void;
  /** Per geteiltem Link (?vorgang=…) angesprungen → einmal hinscrollen. */
  scrollBeimOeffnen?: boolean;
}) {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);

  /** Kurz sichtbare Bestätigung nach dem Kopieren — sonst weiß niemand, ob es geklappt hat. */
  const [kopiert, setKopiert] = useState(false);
  const kopiereNummer = async (nummer: string) => {
    try {
      await navigator.clipboard.writeText(nummer);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 1500);
    } catch {
      // Ohne Zwischenablage-Recht (unsicherer Kontext, alter Browser) bleibt nur der Hinweis.
      window.prompt('Vorgangsnummer kopieren:', nummer);
    }
  };
  useEffect(() => {
    if (scrollBeimOeffnen) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [scrollBeimOeffnen]);

  // Teilbarer Deep-Link auf genau diesen Vorgang: öffnet die KUNDENLISTE, klappt den
  // Kunden inline auf und scrollt zum Vorgang (keine separate Detailseite).
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const url = `${window.location.origin}/customers?customer=${vorgang.customerId}&vorgang=${vorgang.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* Clipboard nicht verfügbar → still ignorieren */ }
  };
  const report = vorgang.reports?.[0];
  // Vollständigkeit für die Kopfzeile — dieselbe Rechnung wie im Dokumenten-Grid,
  // damit man den Stand sieht, ohne den Vorgang aufklappen zu müssen.
  const stand = sollStand(vorgang.files, VORGANG_SOLL);
  const prozent = stand.gesamt > 0 ? Math.round((stand.vorhanden / stand.gesamt) * 100) : 0;

  // „⋯"-Menü mit den selteneren Aktionen; ein Klick daneben schließt es.
  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOffen) return;
    const zu = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOffen(false); };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [menuOffen]);
  // Der VN-Link liegt im Menü — ist er abholbereit, markiert ein Punkt am „⋯" das.
  const [vnStatus, setVnStatus] = useState<string | null>(null);
  const [anfordernOffen, setAnfordernOffen] = useState(false);
  // Roher (unsignierter) Verwendungsnachweis im Vorgang? → „VN-Link anfordern" ist dann überflüssig.
  const hasRawVn = (vorgang.files ?? []).some(f => {
    const c = f.category?.toLowerCase() ?? '';
    return c.includes('verwendungsnachweis') && !c.includes('unterschrieben');
  });
  // Status wird automatisch aus dem Bericht (BAFA-Portal) übernommen — keine manuelle Auswahl
  // (Ausnahme: STATUS_BEI_BAFA, gesetzt per Ein-Klick-Knopf im Dashboard).
  const displayStatus = report?.status ?? '—';
  const versteckeStatusBadges = vorgang.archived || displayStatus === STATUS_BEI_BAFA;
  // Restlaufzeit = Zeit bis zum Ende des Beratungszeitraums (Bericht-Enddatum).
  // Gleiche Logik/Optik wie in der Berichte-Liste (/reports).
  const endDate = report?.endDate || null;
  const remaining = endDate ? getRemainingTimeBadge(endDate, report?.status) : null;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [bafaSubmit, setBafaSubmit] = useState<{ fileId: number; name: string } | null>(null);
  const [bafaSending, setBafaSending] = useState(false);
  const [bafaMsg, setBafaMsg] = useState<string | null>(null);
  // Einreich-Stand: gesendet (eigener Upload) und bestätigt (Eingangsbestätigung der BAFA).
  // Er entscheidet über Beschriftung und Sperre des Knopfes.
  const [bafaStand, setBafaStand] = useState<BafaEinreichStand | null>(null);
  const [bafaEmail, setBafaEmail] = useState('');
  const ladeBafaStand = () => {
    getBafaEinreichStand(vorgang.id).then(setBafaStand).catch(() => setBafaStand(null));
  };
  useEffect(() => { if (open) ladeBafaStand(); /* eslint-disable-next-line */ }, [open, vorgang.id]);

  /** Gibt die abgelegte Bericht-PDF den aktuellen Text noch wieder? Dieselbe Auskunft wie
   *  im Editor — sonst behauptet jede Ansicht etwas anderes. */
  const [fassung, setFassung] = useState<{ pdfAm: string | null; veraltet: boolean } | null>(null);
  useEffect(() => {
    if (!open || !report) { setFassung(null); return; }
    let abgebrochen = false;
    api.get<{ pdfAm: string | null; veraltet: boolean }>(`/api/reports/${report.id}/bericht-stand`)
      .then(st => { if (!abgebrochen) setFassung(st); })
      .catch(() => { if (!abgebrochen) setFassung(null); });
    return () => { abgebrochen = true; };
  }, [open, report?.id, vorgang.files?.length]);

  const doDelete = async () => {
    setDeleting(true);
    try { await deleteVorgang(vorgang.id); onChanged(); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  /** Archivieren blendet den Vorgang aus Aufgaben/Nachforderungen aus — im Unterschied zu
   *  doDelete bleibt er hier in der Kundenhistorie sichtbar, nur mit Plakette. */
  const toggleArchiviert = async () => {
    await updateVorgang(vorgang.id, { archived: !vorgang.archived });
    onChanged();
  };

  const [zipping, setZipping] = useState(false);
  const downloadAll = async () => {
    setZipping(true);
    try { await downloadVorgangZip(vorgang.id, `BAFA_${vorgang.vorgangsnummer || vorgang.id}.zip`); }
    catch (e: any) { alert(e?.message || 'Download fehlgeschlagen'); }
    finally { setZipping(false); }
  };

  const handleBafaSubmit = async () => {
    if (!bafaSubmit) return;
    setBafaSending(true); setBafaMsg(null);
    try {
      // force: der Dialog wird nur mit bewusstem „Erneut senden" geöffnet, wenn schon
      // gesendet/bestätigt wurde — dann ist der Wille zum zweiten Versand belegt.
      const schonEingereicht = !!(bafaStand?.gesendetAm || bafaStand?.bestaetigtAm);
      const res = await submitVnToBafa(vorgang.id, { email: bafaEmail.trim() || undefined, force: schonEingereicht });
      if (res.ok) {
        setBafaMsg('Erfolgreich an BAFA übermittelt.');
        ladeBafaStand();
        setTimeout(() => setBafaSubmit(null), 1500);
      } else if (res.reason === 'verteiler-mail-fehlt') {
        setBafaMsg('Bitte eine E-Mail-Adresse eintragen — dorthin schickt die BAFA die Eingangsbestätigung.');
      } else {
        setBafaMsg(`Nicht bestätigt (${res.reason}) — bitte prüfen. Der VN wurde nicht als eingereicht markiert.`);
      }
    } catch (e: any) {
      setBafaMsg(e?.message || 'Fehler beim Senden an BAFA.');
    } finally { setBafaSending(false); }
  };

  return (
    <div ref={rootRef} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      {/* Kein <button> als Rahmen: Der Kopier-Knopf sitzt darin, und ein Knopf im Knopf ist
          ungültiges HTML — Browser reißen die Verschachtelung auseinander. */}
      <div role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className={`w-full flex items-center gap-2 px-3 py-3.5 text-left cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30 ${open ? 'rounded-t-lg' : 'rounded-lg'}`}>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
        {/* Die Vorgangsnummer wird ständig woanders gebraucht (BAFA-Portal, Mails, Telefon).
            Abtippen ist fehleranfällig — sieben Ziffern, eine verdreht, und der Login scheitert. */}
        {vorgang.vorgangsnummer && (
          <button
            onClick={(e) => { e.stopPropagation(); kopiereNummer(vorgang.vorgangsnummer!); }}
            title={kopiert ? 'Kopiert' : 'Vorgangsnummer kopieren'}
            aria-label={`Vorgangsnummer ${vorgang.vorgangsnummer} kopieren`}
            className="shrink-0 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {kopiert ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        <span className="font-medium text-sm text-slate-900 dark:text-white shrink-0">
          {vorgang.vorgangsnummer || <span className="text-slate-400 italic font-normal">Nummer folgt automatisch</span>}
        </span>
        {report?.berichtstitel && (
          <span className="text-xs text-slate-600 dark:text-slate-300 truncate hidden sm:inline min-w-0" title={report.berichtstitel}>{report.berichtstitel}</span>
        )}
        {vorgang.archived && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
            <Archive className="w-3 h-3" /> Archiviert
          </span>
        )}
        {/* Berater hat per Ein-Klick-Knopf im Dashboard bestätigt "liegt bei BAFA, nichts zu
            tun" — gleiche Machart wie "Archiviert": eigene ruhige Plakette statt der sonst
            weiter alarmfarbenen Restlaufzeit-Badge (Kundenwunsch). */}
        {!vorgang.archived && displayStatus === STATUS_BEI_BAFA && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">
            <Send className="w-3 h-3" /> Bei BAFA zur Prüfung
          </span>
        )}
        {/* Rechts der Zustand auf einen Blick. Der Fortschritt steht ganz außen, damit er
            in allen Zeilen auf derselben Höhe UND derselben Kante sitzt — die Badges davor
            sind unterschiedlich breit und würden ihn sonst hin und her schieben. */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Archivierte Vorgänge UND bestätigt "bei BAFA zur Prüfung": Restlaufzeit-/Status-
              Badges weglassen statt sie weiter alarmfarben (z.B. gelb "In Prüfung" bei
              abgelaufener Frist) anzuzeigen — in beiden Fällen ist für den Berater gerade
              nichts zu tun, unabhängig vom zuletzt bekannten BAFA-Status. Die eigene Plakette
              oben reicht als Hinweis; der volle Status bleibt beim Aufklappen weiterhin
              einsehbar. */}
          {!versteckeStatusBadges && (remaining != null ? (
            <span title={`Beratungszeitraum bis ${endDate} — Restlaufzeit`}
              className={`text-[12px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${remainingBadgeClasses(remaining.color)}`}>
              {remaining.text}
            </span>
          ) : (
            <span title="Kein Beratungszeitraum hinterlegt — Erlaubnis (Beratungsbeginn) hochladen"
              className="text-[12px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap text-slate-400 dark:text-slate-500 border border-dashed border-slate-300 dark:border-slate-600">
              kein Zeitraum
            </span>
          ))}
          {!versteckeStatusBadges && (
            <span className={`text-[12px] px-1.5 py-0.5 rounded-full font-medium ${statusBadge(displayStatus)}`}>{displayStatus}</span>
          )}
          <span title={stand.fehlend.length ? `Es fehlen: ${stand.fehlend.join(', ')}` : 'Alle Soll-Dokumente liegen vor'}
            className="hidden sm:inline w-[104px] text-right text-[12px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {stand.vorhanden}/{stand.gesamt} Dokumente
          </span>
          <span className="hidden sm:block w-14 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
            title={`${stand.vorhanden} von ${stand.gesamt} Dokumenten`}>
            <span
              className={`block h-full rounded-full ${stand.fehlend.length === 0 ? 'bg-green-600' : 'bg-amber-500'}`}
              style={{ width: `${prozent}%` }}
            />
          </span>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200 dark:border-slate-700">
          {/* Eine Hauptaktion links, alles Weitere im „⋯"-Menü rechts. */}
          <div className="flex items-center gap-2">
            {(() => {
              const blueVn = (vorgang.files ?? []).find(
                f => f.category?.toLowerCase().includes('verwendungsnachweis') && f.category?.toLowerCase().includes('unterschrieben'),
              );
              // Die Hauptaktion folgt der Kette (siehe naechsteAktion.ts): Was JETZT dran
              // ist, steht als Knopf da — nicht pauschal „Dokumente anfordern".
              if (!blueVn) {
                const aktion = naechsteAktion({
                  kategorien: (vorgang.files ?? []).map(f => f.category),
                  hatBericht: !!report,
                });
                const knopf = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors';

                if (aktion === 'bericht_senden' && report) {
                  return (
                    <button
                      onClick={() => navigate(`/editor/${report.id}?aktion=bericht-unterschrift`)}
                      title="Öffnet den Bericht und den Versand-Dialog für die Unterschrift"
                      className={knopf}
                    >
                      <Send className="w-3.5 h-3.5" /> Bericht zur Unterschrift senden
                    </button>
                  );
                }
                if (aktion === 'vn_senden' && report) {
                  return (
                    <button
                      onClick={() => navigate(`/editor/${report.id}?aktion=vn-unterschrift`)}
                      title="Öffnet den Bericht und den Versand-Dialog für den Verwendungsnachweis"
                      className={knopf}
                    >
                      <Send className="w-3.5 h-3.5" /> Verwendungsnachweis zur Unterschrift senden
                    </button>
                  );
                }
                // Der Bericht ist unterschrieben zurück, aber es gibt noch kein
                // VN-Formular. Das holt man bei der BAFA — genau das kann der VnButton.
                if (aktion === 'vn_anfordern') {
                  return <VnButton vorgangId={vorgang.id} customerId={vorgang.customerId} active={open} onStatus={setVnStatus} />;
                }
                if (aktion === 'bericht_schreiben' && report) {
                  return (
                    <button onClick={() => navigate(`/editor/${report.id}`)} title="Bericht öffnen und weiterschreiben" className={knopf}>
                      <FileText className="w-3.5 h-3.5" /> Bericht schreiben
                    </button>
                  );
                }
                return (
                  <button
                    onClick={() => setAnfordernOffen(true)}
                    title="Dem Kunden einen Link schicken, über den er Daten und Dokumente selbst hinterlegt"
                    className={knopf}
                  >
                    <Send className="w-3.5 h-3.5" /> Dokumente anfordern
                  </button>
                );
              }
              // Drei Zustände: offen → gesendet (eigener Upload, Beleg fehlt) → bestätigt
              // (Eingangsbestätigung der BAFA ist da). Datum steht im Tooltip, nicht auf dem Knopf.
              const bestaetigt = !!bafaStand?.bestaetigtAm;
              const gesendet = !bestaetigt && !!bafaStand?.gesendetAm;
              // Ein abgeschlossener/ausgezahlter Vorgang braucht keine (erneute) Einreichung mehr —
              // auch wenn der BAFA-Einreichstand nie erfasst wurde (Vorgang 1302286: „Zahlung
              // erfolgt", aber gesendetAm/bestaetigtAm leer geblieben, zeigte trotzdem "VN EINREICHEN").
              const vorgangAbgeschlossen = isGreenStatus(displayStatus);
              const zeit = (iso?: string | null) =>
                iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
              const oeffnen = () => {
                setBafaMsg(null);
                setBafaEmail(bafaStand?.verteilerEmail ?? '');
                setBafaSubmit({ fileId: blueVn.id, name: blueVn.originalName });
              };
              if (bestaetigt || gesendet) {
                return (
                  <span className="inline-flex items-center gap-1">
                    <span
                      title={bestaetigt
                        ? `BAFA-Eingangsbestätigung vom ${zeit(bafaStand?.bestaetigtAm)}`
                        : `Gesendet ${zeit(bafaStand?.gesendetAm)} — wartet auf die Eingangsbestätigung der BAFA`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${
                        bestaetigt
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {bestaetigt ? <Check className="w-3.5 h-3.5" /> : <Upload className="w-3.5 h-3.5" />}
                      {bestaetigt ? 'VN BESTÄTIGT' : 'VN GESENDET'}
                    </span>
                    {!vorgangAbgeschlossen && (
                      <button onClick={oeffnen} title="Verwendungsnachweis erneut an die BAFA senden"
                        className="text-[12px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 underline decoration-dotted">
                        erneut
                      </button>
                    )}
                  </span>
                );
              }
              if (vorgangAbgeschlossen) {
                return (
                  <span
                    title="Vorgang ist bereits abgeschlossen — keine Einreichung mehr nötig"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                  >
                    <Check className="w-3.5 h-3.5" /> VORGANG ABGESCHLOSSEN
                  </span>
                );
              }
              return (
                <button
                  onClick={oeffnen}
                  title="Unterschriebenen Verwendungsnachweis an die BAFA senden"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" /> VN EINREICHEN
                </button>
              );
            })()}
            {/* Alles Seltenere gebündelt — der Vorgang zeigt sonst fünf gleichrangige Knöpfe. */}
            <div ref={menuRef} className="relative ml-auto">
              <button
                onClick={() => setMenuOffen(o => !o)}
                aria-label="Weitere Aktionen"
                aria-expanded={menuOffen}
                className="relative p-2 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
                {vnStatus === 'bereit' && (
                  <span title="VN-Link ist abholbereit" className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-600" />
                )}
              </button>
              {menuOffen && (
                <div className="absolute right-0 top-full mt-1 w-60 py-1 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg z-30">
                  {report && (
                    <button onClick={() => { setMenuOffen(false); navigate(`/editor/${report.id}`); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                      <FileText className="w-3.5 h-3.5" /> Bericht öffnen
                    </button>
                  )}
                  <button onClick={() => { setMenuOffen(false); downloadAll(); }} disabled={zipping}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50">
                    <Download className="w-3.5 h-3.5" /> {zipping ? 'ZIP wird erstellt…' : 'Alle Dokumente herunterladen'}
                  </button>
                  <VnButton vorgangId={vorgang.id} customerId={vorgang.customerId} active={open} vnUploaded={hasRawVn} variant="menu" onStatus={setVnStatus} />
                  {/* Steht die Hauptaktion woanders, bleibt das Anfordern hier erreichbar. */}
                  <button onClick={() => { setMenuOffen(false); setAnfordernOffen(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                    <Send className="w-3.5 h-3.5" /> Dokumente anfordern
                  </button>
                  <button onClick={copyLink}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Link2 className="w-3.5 h-3.5" />}
                    {copied ? 'Link kopiert' : 'Link kopieren'}
                  </button>
                  <button onClick={() => { setMenuOffen(false); toggleArchiviert(); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                    {vorgang.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    {vorgang.archived ? 'Wiederherstellen' : 'Archivieren'}
                  </button>
                  <button onClick={() => { setMenuOffen(false); setConfirmDelete(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-600 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Löschen
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Text geändert, PDF nicht nachgezogen: Hier fällt es auf, auch wenn niemand den
              Editor öffnet — und beim verschickten Bericht hängt eine falsche Fassung beim
              Kunden. */}
          {fassung?.veraltet && report && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2">
              <span className="text-sm" aria-hidden="true">⚠️</span>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-red-800 dark:text-red-300">
                  Es gab Änderungen seit der letzten Fassung
                  {fassung.pdfAm && ` vom ${new Date(fassung.pdfAm).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(', ', ' um ')} Uhr`}.
                </p>
              </div>
              <button
                onClick={() => navigate(`/editor/${report.id}`)}
                className="shrink-0 px-2.5 py-1 text-xs font-medium rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Bericht-PDF neu erzeugen
              </button>
            </div>
          )}
          <DocumentGrid title="Vorgang-Dokumente" level={{ vorgangId: vorgang.id }} soll={VORGANG_SOLL} categories={VORGANG_CATEGORIES} onChanged={onChanged} collapse="wenn-vollstaendig" />
        </div>
      )}

      {anfordernOffen && (
        <DataRequestModal
          customerId={vorgang.customerId}
          prefillEmail={customerEmail ?? ''}
          onClose={() => setAnfordernOffen(false)}
          onSuccess={onChanged}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Vorgang löschen?</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              „{vorgang.vorgangsnummer || 'Vorgang'}" wird gelöscht. Die zugehörigen Dokumente sind danach nicht mehr über diesen Vorgang sichtbar.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">Abbrechen</button>
              <button onClick={doDelete} disabled={deleting}
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50">{deleting ? 'Lösche…' : 'Löschen'}</button>
            </div>
          </div>
        </div>
      )}

      {bafaSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !bafaSending && setBafaSubmit(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Verwendungsnachweis an BAFA senden?</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Vorgang „{vorgang.vorgangsnummer || '—'}" · Datei: {bafaSubmit.name}
            </p>
            {(bafaStand?.gesendetAm || bafaStand?.bestaetigtAm) && (
              <p className="text-xs mb-3 px-2.5 py-2 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                {bafaStand?.bestaetigtAm
                  ? 'Die BAFA hat den Eingang dieses Vorgangs bereits bestätigt. Erneutes Senden legt eine zweite Fassung im Portal ab.'
                  : 'Dieser Verwendungsnachweis wurde bereits gesendet, die Eingangsbestätigung steht noch aus.'}
              </p>
            )}
            <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">
              Eingangsbestätigung an
            </label>
            <input
              type="email"
              value={bafaEmail}
              onChange={e => setBafaEmail(e.target.value)}
              placeholder="adresse@die.app"
              className="w-full mb-1 px-2.5 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white outline-none focus:border-blue-500"
            />
            <p className="text-[13px] text-slate-400 mb-3">
              Nur für diesen Versand — wird nicht beim Kunden gespeichert. Ohne Adresse schickt die BAFA keine Bestätigung.
            </p>
            {bafaMsg && <p className="text-sm mb-3 text-amber-700 dark:text-amber-400">{bafaMsg}</p>}
            <div className="flex justify-between gap-2">
              <button
                onClick={async () => {
                  const { url } = await fetchFileBlob(bafaSubmit.fileId);
                  window.open(url, '_blank');
                }}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >Vorschau</button>
              <div className="flex gap-2">
                <button onClick={() => setBafaSubmit(null)} disabled={bafaSending}
                  className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">Abbrechen</button>
                <button onClick={handleBafaSubmit} disabled={bafaSending}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50">{bafaSending ? 'Sende…' : 'An BAFA senden'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
